import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const { pathname } = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 100;
      setIsScrolled(scrolled);

      // Calculate scroll progress
      const h = document.documentElement;
      const progress = (h.scrollTop / (h.scrollHeight - h.clientHeight || 1)) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <>
      {/* Scroll Progress */}
      <div 
        className="fixed top-0 left-0 h-[2px] bg-gradient-to-r from-amber-400 to-green-400 z-[60]"
        style={{ width: `${scrollProgress}%` }}
      />

      {/* Header */}
      <header 
        id="site-header" 
        className={`fixed top-0 inset-x-0 z-50 backdrop-blur-md border-b bg-black/70 border-orange-900 transition-transform duration-300 ${
          isScrolled ? '-translate-y-full' : 'translate-y-0'
        }`}
      >
        <nav className="sm:px-6 lg:px-8 h-16 max-w-full mr-auto ml-auto pr-4 pl-4">
          <div className="h-full flex items-center justify-between">
            {/* Brand */}
            <Link 
              to="/" 
              className="flex items-center focus:outline-none focus:ring-2 focus:ring-amber-500 rounded-md"
            >
              <img
                src="/logo.png"
                alt="The Alpha Consult logo"
                className="h-10 w-10 object-cover md:h-11 md:w-11"
                loading="eager"
              />
            </Link>

            {/* Desktop Menu */}
            <nav className="hidden md:flex items-center gap-2 lg:gap-4 xl:gap-6">
              {pathname !== '/' && (
                <Link 
                  to="/" 
                  className="px-3 py-2 rounded-md hover:bg-white/5 transition-colors font-nunito"
                >
                  Overview
                </Link>
              )}
              {pathname !== '/services' && (
                <Link 
                  to="/services" 
                  className="px-3 py-2 rounded-md hover:bg-white/5 transition-colors font-nunito"
                >
                  Services
                </Link>
              )}
              {pathname !== '/process' && (
                <Link 
                  to="/process" 
                  className="px-3 py-2 rounded-md hover:bg-white/5 transition-colors font-nunito"
                >
                  Process
                </Link>
              )}
              {pathname !== '/pricing' && (
                <Link 
                  to="/pricing" 
                  className="px-3 py-2 rounded-md hover:bg-white/5 transition-colors font-nunito"
                >
                  Plans
                </Link>
              )}
              {pathname !== '/contact' && (
                <Link 
                  to="/contact" 
                  className="px-3 py-2 rounded-md hover:bg-white/5 transition-colors font-nunito"
                >
                  Request a proposal
                </Link>
              )}
            </nav>

            {/* Actions */}
            <div className="flex items-center gap-3">
              {pathname !== '/pricing' && (
                <Link 
                  to="/pricing" 
                  className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-orange-100 text-black hover:bg-orange-200 transition-all hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-amber-500 font-nunito"
                >
                  Get a quote
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="1.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    className="w-4 h-4"
                  >
                    <path d="M5 12h14"></path>
                    <path d="m12 5 7 7-7 7"></path>
                  </svg>
                </Link>
              )}
              <button 
                onClick={toggleMobileMenu}
                aria-expanded={mobileMenuOpen}
                className="md:hidden p-2 rounded-md transition-colors hover:bg-orange-900 focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="22" 
                  height="22" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  className={`w-5 h-5 ${mobileMenuOpen ? 'hidden' : 'block'}`}
                >
                  <path d="M4 5h16"></path>
                  <path d="M4 12h16"></path>
                  <path d="M4 19h16"></path>
                </svg>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  width="22" 
                  height="22" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  className={`w-5 h-5 ${mobileMenuOpen ? 'block' : 'hidden'}`}
                >
                  <path d="M18 6 6 18"></path>
                  <path d="m6 6 12 12"></path>
                </svg>
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Panel */}
      <div 
        className={`fixed inset-x-0 top-16 z-40 bg-black/90 backdrop-blur-md border-b border-orange-900 ${
          mobileMenuOpen ? 'block' : 'hidden'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="grid gap-1">
            {pathname !== '/' && (
              <Link 
                to="/" 
                onClick={closeMobileMenu}
                className="px-3 py-2 rounded-md hover:bg-white/5 transition-colors font-nunito"
              >
                Overview
              </Link>
            )}
            {pathname !== '/services' && (
              <Link 
                to="/services" 
                onClick={closeMobileMenu}
                className="px-3 py-2 rounded-md hover:bg-white/5 transition-colors font-nunito"
              >
                Services
              </Link>
            )}
            {pathname !== '/process' && (
              <Link 
                to="/process" 
                onClick={closeMobileMenu}
                className="px-3 py-2 rounded-md hover:bg-white/5 transition-colors font-nunito"
              >
                Process
              </Link>
            )}
            {pathname !== '/pricing' && (
              <Link 
                to="/pricing" 
                onClick={closeMobileMenu}
                className="px-3 py-2 rounded-md hover:bg-white/5 transition-colors font-nunito"
              >
                Plans
              </Link>
            )}
            {pathname !== '/contact' && (
              <Link 
                to="/contact" 
                onClick={closeMobileMenu}
                className="px-3 py-2 rounded-md hover:bg-white/5 transition-colors font-nunito"
              >
                Request a proposal
              </Link>
            )}
          </nav>
        </div>
      </div>
    </>
  );
};

export default Header;
